import type { FundingEligibilityCategoryGroup } from "@/lib/apply-now/funding-eligibility-categories";

export const HOME_SIGNUP_URL = "https://app.wayflyer.com/authn/signup";

/** Public Trustpilot profile backing the hero star rating. */
export const HOME_TRUSTPILOT_URL =
  "https://www.trustpilot.com/review/wayflyer.com";

/** Literal Figma figure — intentionally decoupled from TOTAL_DEPLOYED_USD. */
export const HOME_DEPLOYED_CAPITAL_USD = 7_083_700_000;

/** Figma 6086:28468 uses 7,000+ literally (not TOTAL_BUSINESSES_FUNDED). */
export const HOME_TRUSTED_BUSINESSES_COUNT = 7000;

export interface LiveHomeFaq {
  question: string;
  answer: string;
}

export interface LiveHomeCopy {
  signupUrl: string;
  deployedCapitalUsd: number;
  trustedBusinessesCount: number;
  hero: {
    eyebrowLabel: string;
    titleLead: string;
    titleSubtitle: string;
    descriptionLine1: string;
    descriptionLine2: string;
    emailPlaceholder: string;
    ctaLabel: string;
    ratingMobile: string;
    ratingDesktop: string;
    /** Trustpilot profile the rating links to. */
    ratingUrl: string;
    /** Accessible name for the icon-only Trustpilot link. */
    ratingLinkLabel: string;
  };
  chapterLabels: {
    applicationProcess: string;
    terms: string;
    customers: string;
    underwriting: string;
    faqs: string;
  };
  trustedGrid: {
    titleSuffix: string;
    titleShort: string;
    ctaLabel: string;
    ctaPath: string;
  };
  productPreviewSteps: string[];
  fundingEligibility: {
    headingLead: string;
    headingEmphasis: string;
    step01Title: string;
    step01Body: string;
    step02Title: string;
    /** Step 02 title when the user is ineligible (Figma: "Stay in the loop"). */
    step02TitleIneligible: string;
    step02BodyBeforeComplete: string;
    /** Default step 02 body below the lg breakpoint (Figma Mobile). */
    step02BodyMobile: string;
    step02BodyAfterComplete: string;
    /** Shown on step 02 when ineligible. */
    step02BodyIneligible: string;
    ineligibleSubmitToastTitle: string;
    ineligibleSubmitToastDescription: string;
    step03Title: string;
    /** Step 03 title when the user is ineligible (Figma: "Know what we look for"). */
    step03TitleIneligible: string;
    step03Body: string;
    /** Prefix before the eligibility criteria link on ineligible step 03. */
    step03BodyIneligiblePrefix: string;
    eligibilityCriteriaLinkText: string;
    /** Suffix after the eligibility criteria link on ineligible step 03. */
    step03BodyIneligibleSuffix: string;
    eligibilityCriteriaLinkHref: string;
    estimatedFundingLabel: string;
    estimatedFundingSuccessLabel: string;
    /** Prefix shown before the cap once both bounds hit MAX_ADVANCE_EUR (e.g. "up to "). */
    estimatedFundingUpToPrefix: string;
    /** Estimate card copy when ineligible. Supports {country} and {threshold}. */
    ineligibleMessage: string;
    ineligibleRevenueLabel: string;
    estimatePrompt: string;
    /** Inline hint below the revenue input when the value exceeds the sanity bound. */
    congratsTitle: string;
    congratsSubtitle: string;
    emailPlaceholder: string;
    /** Email placeholder on ineligible step 02 (Figma: "Your email"). */
    ineligibleEmailPlaceholder: string;
    emailLabel: string;
    applyNowLabel: string;
    /** CTA label on ineligible step 02 (Figma: "Get updates"). */
    ineligibleApplyLabel: string;
    sendingLabel: string;
    submitToastTitle: string;
    submitToastDescription: string;
    formMonthlyRevenueLabel: string;
    formMonthlyRevenueAria: string;
    formMonthlyRevenuePlaceholder: string;
    formCategoryLabel: string;
    formCategoryAria: string;
    formCategoryPlaceholder: string;
    formCountryLabel: string;
    formCountryAria: string;
    formCountryPlaceholder: string;
    categoryGroups: FundingEligibilityCategoryGroup[];
    countries: Array<{ value: string; label: string }>;
  };
  terms: {
    headingMobile: string;
    headingDesktop: string;
    pricingTitle: string;
    pricingDescription: string;
    repaymentTitle: string;
    repaymentDescription: string;
    equityLead: string;
    equityRest: string;
    spendLead: string;
    spendRest: string;
    guaranteesLead: string;
    guaranteesRest: string;
    mockFinancingAmountLabel: string;
    mockFeeLabel: string;
    mockTotalLabel: string;
    mockBusinessFundingLabel: string;
    mockNoPersonalLiabilityLabel: string;
    mockFinancingAmount: string;
    mockFeeAmount: string;
    mockTotalAmount: string;
  };
  underwriting: {
    heading: string;
    subheading: string;
    steps: Array<{
      number: "01" | "02" | "03";
      eyebrow: string;
      title: string;
    }>;
  };
  faqTitle: string;
  faqs: LiveHomeFaq[];
  footerCta: {
    title: string;
    primaryCtaLabel: string;
    secondaryCtaLabel: string;
    secondaryCtaPath: string;
  };
}
